import {Phone, Calendar, Sun, Shield, Check, MapPin, Thermometer, Award, Eye} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const WindowTintSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    // Add FAQ Schema
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does window tint cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Standard tint: $250-$400. Ceramic tint: $450-$800. Pricing varies by vehicle size and tint quality. SUVs and luxury vehicles may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "What's the darkest legal tint in California?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Front side windows: 70% or lighter (must allow 70% light transmission). Rear side and back windows: any darkness allowed. Windshield: 4-inch strip only. We ensure all installations comply with California law."
          }
        },
        {
          "@type": "Question",
          "name": "How long does window tint last?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ceramic tint lasts 10+ years. Standard film lasts 5-7 years. Quality depends on film grade, installation, and maintenance. Premium ceramic tint won't fade or purple."
          }
        },
        {
          "@type": "Question",
          "name": "Is ceramic tint worth the extra cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, especially in San Diego. Ceramic tint blocks 99% UV rays and rejects significantly more heat than standard film while maintaining clarity and lasting 10+ years without fading."
          }
        },
        {
          "@type": "Question",
          "name": "Can window tint be removed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Professional tint removal takes 2-3 hours and leaves glass clean without damage or adhesive residue when properly performed."
          }
        },
        {
          "@type": "Question",
          "name": "How long does window tint installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most vehicles: 2-4 hours. Large SUVs and complex rear windows may take longer. Tint is ready to use after 3-5 days curing time."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Set meta tags
    document.title = "Window Tint San Diego | Professional Ceramic Tinting & Heat Rejection"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert window tint installation in San Diego. Ceramic tint, 99% UV protection, heat rejection, lifetime warranty. Legal compliance guaranteed. Serving Vista, Carlsbad, Oceanside. Free quotes.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/window-tint-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Window Tint San Diego - Professional Ceramic Tinting & Heat Rejection</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your vehicle interior and enhance comfort with professional window tint installation in San Diego. Our installers deliver careful ceramic tint application—blocking 99% UV rays, rejecting heat, reducing glare, and providing privacy while maintaining visibility and legal compliance.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving San Diego County including Vista, Carlsbad, Oceanside, Encinitas, and surrounding areas. We specialize in premium ceramic window tint with lifetime warranties, superior heat rejection, and California-legal installation on all vehicle types from daily drivers to luxury exotic vehicles.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Tint Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Window Tint is Essential in San Diego</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              San Diego's year-round sunshine and intense UV radiation make window tint essential for vehicle protection and occupant comfort. Without tint, interior temperatures regularly exceed 140°F on summer days, causing dashboard cracking, seat fading, and dangerous heat buildup that poses health risks to passengers and pets.
            </p>
            <p>
              Professional <a href="/window-tinting" className="text-cyan-400 hover:underline">ceramic window tint</a> blocks up to 99% of harmful UV rays preventing interior fade and skin damage during your daily commute. Premium ceramic technology rejects 60-80% of solar heat without using metal—maintaining excellent cell phone reception, GPS, and radio signals unlike older metallized films. Your air conditioning works less, fuel efficiency improves, and cabin comfort dramatically increases.
            </p>
            <p>
              Beyond heat rejection, window tint provides privacy, security, and glare reduction. Darker rear windows prevent thieves from viewing valuables inside your vehicle—particularly important in high-traffic areas like <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">downtown San Diego</a>, shopping centers, and beach parking lots. Reduced glare improves driving safety during San Diego's famous sunsets and bright midday sun.
            </p>
            <p>
              We install California-legal window tint with proper darkness levels for front side windows (70% minimum light transmission) and any darkness for rear windows. Many clients combine tint with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">paint protection film</a> and <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> for complete vehicle protection and aesthetic enhancement in one visit to our <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista facility</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Window Tint Pricing in San Diego</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Standard Tint</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$250 - $400</div>
              <p className="text-gray-400 text-sm mb-6">Quality dyed or hybrid film</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All side and rear windows</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>UV protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Privacy and glare reduction</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>5-7 year lifespan</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Ceramic Tint</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$450 - $800</div>
              <p className="text-gray-400 text-sm mb-6">Premium nano-ceramic technology</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>99% UV ray blockage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>60-80% heat rejection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>No signal interference</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Lifetime warranty, won't fade</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Windshield Tint</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$150 - $300</div>
              <p className="text-gray-400 text-sm mb-6">Legal 4-inch strip or full windshield</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>California legal compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Reduces dashboard glare</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front cabin heat reduction</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Ceramic heat rejection</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400">
            Package discounts available when combined with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a> or <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a>. Starting prices shown above are based on vehicle size. Exact pricing will be determined after a free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Ceramic vs Standard */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Ceramic Tint vs Standard Tint</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">RECOMMENDED</div>
              <h3 className="text-3xl font-bold mb-6 text-white">Ceramic Tint</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">99% UV Blockage</span>
                    <p className="text-sm text-gray-400">Maximum skin and interior protection</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">60-80% Heat Rejection</span>
                    <p className="text-sm text-gray-400">Dramatically cooler cabin temperatures</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">No Signal Interference</span>
                    <p className="text-sm text-gray-400">Cell phone, GPS, radio work perfectly</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Won't Fade or Purple</span>
                    <p className="text-sm text-gray-400">Maintains color and clarity for 10+ years</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Lifetime Warranty</span>
                    <p className="text-sm text-gray-400">Manufacturer defect coverage</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-3xl font-bold mb-6 text-white">Standard Tint</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Basic UV Protection</span>
                    <p className="text-sm text-gray-400">Blocks some UV rays</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Moderate Heat Rejection</span>
                    <p className="text-sm text-gray-400">30-40% heat reduction</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">May Cause Signal Issues</span>
                    <p className="text-sm text-gray-400">Metallized film can interfere with signals</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Can Fade Over Time</span>
                    <p className="text-sm text-gray-400">May turn purple after 5-7 years</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-white">Limited Warranty</span>
                    <p className="text-sm text-gray-400">Typically 3-5 year coverage</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 mt-8 max-w-2xl mx-auto">
            For San Diego's intense sun and heat, we strongly recommend ceramic tint. The additional investment pays for itself through better comfort, longer lifespan, and superior UV protection.
          </p>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Window Tint Benefits for San Diego Drivers</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Thermometer className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Heat Rejection</h3>
              <p className="text-gray-300">Ceramic tint blocks 60-80% of solar heat, keeping interiors 15-20°F cooler in San Diego summers. Reduced AC usage improves fuel efficiency.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sun className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">UV Protection</h3>
              <p className="text-gray-300">99% UV ray blockage protects your skin from sun damage during commutes and prevents interior fading, cracking, and deterioration.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Eye className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Privacy & Security</h3>
              <p className="text-gray-300">Darker rear windows prevent thieves from seeing valuables inside while providing privacy for passengers and cargo.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Shatter Protection</h3>
              <p className="text-gray-300">In accidents, tint film holds broken glass together reducing injury from flying shards and providing added security against break-ins.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Window Tint Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Shade Selection</h3>
                <p className="text-gray-300">We discuss your goals (heat rejection, privacy, aesthetics), review California legal limits, and show physical tint samples so you can visualize different darkness levels on your vehicle. We'll recommend shade percentages based on your needs: 70% (lightest legal front), 50%, 35%, 20%, 5% (limo dark rear). Most San Diego clients choose 35% rear windows for optimal privacy and heat rejection.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Window Preparation & Cleaning</h3>
                <p className="text-gray-300">Thorough cleaning is essential for bubble-free tint installation. We remove door panels when necessary for proper access, then meticulously clean windows inside and out using razor blades to remove debris, professional glass cleaner, and lint-free towels. Any old tint, adhesive residue, or contaminants are completely removed before new film application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Professional Film Application</h3>
                <p className="text-gray-300">Using precision-cut patterns specific to your vehicle's window dimensions, we apply premium ceramic tint film with professional installation liquid for perfect positioning. Each window is squeegeed meticulously to remove all water and air bubbles, heat-treated for permanent adhesion, and edges sealed properly. Complex rear windows with defrost lines receive extra attention to prevent damage.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Check & Care Instructions</h3>
                <p className="text-gray-300">Final inspection ensures bubble-free installation, perfect edge seal, and complete coverage. We provide detailed care instructions: avoid rolling windows down for 3-5 days (curing period), use ammonia-free glass cleaner only, and clean with soft microfiber cloths. Lifetime warranty documentation included for ceramic tint installations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Window Tint FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does window tint cost in San Diego?</h3>
              <p className="text-gray-300">Standard window tint: $250-$400 for most vehicles. Premium ceramic tint: $450-$800 depending on vehicle size. Windshield tint strip: $150-$300. Large SUVs and vehicles with complex rear windows may cost more. We provide exact quotes after inspecting your vehicle. Package discounts available when combined with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a> or <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a>.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What's the darkest legal tint in California?</h3>
              <p className="text-gray-300">California law requires front side windows to allow 70% or more light transmission (70% tint is lightest/most see-through). Rear side windows and back window can be any darkness you prefer—most choose 20-35% for privacy and heat rejection. Windshield tinting limited to top 4-inch strip only. We ensure all installations comply with California Vehicle Code 26708 to avoid tickets.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does window tint last?</h3>
              <p className="text-gray-300">Premium ceramic tint lasts 10+ years in San Diego without fading, purpling, or bubbling. Standard dyed or hybrid film lasts 5-7 years before showing signs of degradation. Longevity depends on film quality, professional installation, and maintenance. Ceramic tint is worth the investment for San Diego's intense UV environment—it maintains appearance and performance for over a decade.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is ceramic tint worth the extra cost?</h3>
              <p className="text-gray-300">Absolutely, especially in San Diego. Ceramic tint blocks 99% of UV rays (standard blocks 40-60%) and rejects significantly more heat (60-80% vs 30-40%). It won't fade or purple, doesn't interfere with electronics, and lasts 10+ years with lifetime warranty. The superior comfort, interior protection, and longevity make ceramic tint the best value despite higher upfront cost.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can window tint be removed?</h3>
              <p className="text-gray-300">Yes. Professional tint removal takes 2-3 hours and leaves glass perfectly clean without damage or adhesive residue when properly performed. We use steaming and specialized solutions to safely separate film from glass. Old faded or bubbling tint should be professionally removed before new tint installation to ensure proper adhesion and appearance.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does window tint installation take?</h3>
              <p className="text-gray-300">Most vehicles: 2-4 hours for all side and rear windows. Large SUVs, trucks, and vehicles with complex rear windows may take 4-6 hours. Tint requires 3-5 days curing time before rolling windows down—we provide specific instructions based on San Diego weather conditions. Do not roll windows down during curing period to prevent damage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will window tint void my vehicle warranty?</h3>
              <p className="text-gray-300">No. Window tint is a cosmetic addition that does not void factory warranties when professionally installed. Some manufacturers (Tesla, Rivian) even recommend window tint for UV and heat protection. Proper installation does not damage glass, defrost lines, or window mechanisms.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does ceramic tint interfere with cell phones or GPS?</h3>
              <p className="text-gray-300">No. Ceramic tint uses nano-ceramic particles (not metal) so it does not block cell phone signals, GPS, satellite radio, or wireless key fobs. Older metallized tints cause interference—another reason we recommend premium ceramic technology for San Diego installations.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I tint my windshield in California?</h3>
              <p className="text-gray-300">California law allows a 4-inch strip across the top of the windshield (AS-1 line). Full windshield tinting is illegal in California. Many clients add the legal 4-inch ceramic strip for glare reduction and dashboard protection. Some choose 70% ceramic on the entire windshield for heat rejection (technically not fully legal but minimal enforcement).</p>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Quote Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmbeddedQuoteForm
            defaultService="window-tint"
            heading="Get a Free Window Tint Quote"
            subheading="Tell us your vehicle make, model, and tint goals. Our Vista team will provide a transparent quote — ceramic tint, standard film, or windshield strip."
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Maximize your vehicle protection and comfort with these complementary services:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/window-tinting" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tinting Services</h3>
              <p className="text-gray-400 text-sm">Complete window tint guide with film technology, legal requirements, and installation information</p>
            </a>
            <a href="/chrome-delete" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete Services</h3>
              <p className="text-gray-400 text-sm">Black out chrome trim for a sleek, modern look that complements your tinted windows perfectly</p>
            </a>
            <a href="/paint-protection-film" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film</h3>
              <p className="text-gray-400 text-sm">Protect your paint with self-healing PPF while window tint protects your interior from UV damage</p>
            </a>
            <a href="/bmw-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">BMW PPF San Diego</h3>
              <p className="text-gray-400 text-sm">BMW owners often pair ceramic tint with paint protection film in one Vista visit</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Window Tint Services Across San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Based in <a href="/ppf-vista" className="text-cyan-400 hover:underline font-semibold">Vista</a>, we provide professional ceramic window tint installation throughout San Diego County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop Location)</li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Poway</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Beat the San Diego Heat with Professional Window Tint</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free window tint consultation. View shade samples and get exact pricing. Same-day installation often available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Tint Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default WindowTintSanDiegoPage
